import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import Header from './Header';
import { useState, useEffect } from 'react';
import { useFilterStore } from '../../stores/filter-store';
import { trackingApi, shipmentItemApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';
import { useStoreStore } from '../../stores/store-store';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import { useToastStore } from '../../stores/toast-store';

export function LayoutWrapper() {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatus, setSelectedStatus] = useState('outstanding');
  const [selectedSubStatus, setSelectedSubStatus] = useState('on_its_way');
  const [isTakeInActive, setIsTakeInActive] = useState(false);
  const [isShipActive, setIsShipActive] = useState(false);
  const [rightTableOrders, setRightTableOrders] = useState<any[]>([]);
  // Mirror split view state from Operation page via window events
  const [isToCenterActive, setIsToCenterActive] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showShipmentSubmit, setShowShipmentSubmit] = useState(false);
  const [shipmentItems, setShipmentItems] = useState<any[]>([]);
  const [originalShipmentItems, setOriginalShipmentItems] = useState<any[]>([]);
  const [isShipmentSubmit, setIsShipmentSubmit] = useState(false);
  // const [isSubmitting, setIsSubmitting] = useState(false);
  const { setFulfillmentStatus, setFulfillmentSubStatus } = useFilterStore();
  const { token } = useAuthStore();
  const { currentStore } = useStoreStore();
  const { addToast } = useToastStore();

  const normalizeOperationSubStatus = (status: string | null, subStatus: string | null) => {
    if (status === 'destination_warehouse' && subStatus === 'missing') {
      return 'unconfirmed';
    }

    return subStatus;
  };
  
  // Determine which tabs to show based on the current page
  const showFulfillmentTabs = pathname === '/fulfillment';
  const showOperationTabs = pathname === '/operation';
  const showReportTabs = pathname === '/report';
  const isDashboard = pathname === '/dashboard';
  
  // Determine if we should show Take-in button (Operation page, In Facility tab, In Bound sub-status)
  const showTakeInButton = showOperationTabs && selectedStatus === 'in_facility' && selectedSubStatus === 'in_bound';
  
  // Determine if we should show Ship button (Operation page, In Facility tab, In Facility sub-status)
  const showShipButton = showOperationTabs && selectedStatus === 'in_facility' && selectedSubStatus === 'in_facility';
  
  // Determine if we should show Outbound submit button (Operation page, In Facility tab, Outbound sub-status)
  const showOutboundSubmit = showOperationTabs && selectedStatus === 'in_facility' && selectedSubStatus === 'outbound';

  // Get initial status from URL params (for navigation from dashboard)
  useEffect(() => {
    // Report page uses its own reportTab param and a different default
    if (showReportTabs) {
      const reportTabFromUrl = searchParams.get('reportTab') || 'tappykash';
      const tappySubFromUrl = searchParams.get('tappySub') || 'capture';
      const voucherSubFromUrl = searchParams.get('voucherSub') || 'unassign';
      setSelectedStatus(reportTabFromUrl);
      if (reportTabFromUrl === 'voucher') {
        setSelectedSubStatus(voucherSubFromUrl);
      } else {
        setSelectedSubStatus(tappySubFromUrl);
      }
      return;
    }
    const tabFromUrl = searchParams.get('tab');
    const subStatusFromUrl = normalizeOperationSubStatus(searchParams.get('tab'), searchParams.get('subStatus'));

    if (tabFromUrl) {
      console.log('Setting status from URL:', tabFromUrl);
      setSelectedStatus(tabFromUrl);

      if (showFulfillmentTabs) {
        setFulfillmentStatus(tabFromUrl);
      }

      // For operation tabs, ensure we preserve the sub-status when returning
      if (showOperationTabs) {
        if (subStatusFromUrl) {
          console.log('Preserving sub-status from URL:', subStatusFromUrl);
          setSelectedSubStatus(subStatusFromUrl);
        } else {
          // Set appropriate default sub-status based on main status
          if (tabFromUrl === 'in_facility') {
            setSelectedSubStatus('in_bound');
          } else if (tabFromUrl === 'awaiting') {
            setSelectedSubStatus('on_its_way');
          } else if (tabFromUrl === 'destination_warehouse') {
            setSelectedSubStatus('inbound');
          } else if (tabFromUrl === 'distribution_center') {
            setSelectedSubStatus('inbound');
          } else if (tabFromUrl === 'delivered') {
            setSelectedSubStatus('delivered');
          } else {
            setSelectedSubStatus('on_its_way');
          }
        }
      }
    }
  }, [searchParams, showFulfillmentTabs, showOperationTabs, showReportTabs, setFulfillmentStatus]);

  // Listen for right table orders updates from SplitOrdersTable
  useEffect(() => {
    const handleRightTableOrdersUpdate = (event: CustomEvent) => {
      setRightTableOrders(event.detail.orders);
    };

    window.addEventListener('right-table-orders-update', handleRightTableOrdersUpdate as EventListener);
    
    return () => {
      window.removeEventListener('right-table-orders-update', handleRightTableOrdersUpdate as EventListener);
    };
  }, []);

  // Track To Center split activation
  useEffect(() => {
    const onActivate = () => setIsToCenterActive(true);
    const onDeactivate = () => setIsToCenterActive(false);
    window.addEventListener('to-center-clicked', onActivate);
    window.addEventListener('sub-status-clicked', onDeactivate as EventListener);
    return () => {
      window.removeEventListener('to-center-clicked', onActivate);
      window.removeEventListener('sub-status-clicked', onDeactivate as EventListener);
    };
  }, []);

  // Listen for shipment items updates from SplitShipmentItemsTable
  useEffect(() => {
    const handleShipmentItemsUpdate = (event: CustomEvent) => {
      setShowShipmentSubmit(event.detail.hasItems);
      if (event.detail.hasItems) {
        setShipmentItems(event.detail.items || []);
      }
    };

    window.addEventListener('shipment-items-selected', handleShipmentItemsUpdate as EventListener);
    
    return () => {
      window.removeEventListener('shipment-items-selected', handleShipmentItemsUpdate as EventListener);
    };
  }, []);

  // Listen for shipment submit confirm event from SplitShipmentItemsTable
  useEffect(() => {
    const handleShipmentSubmitConfirm = (event: CustomEvent) => {
      if (event.detail?.action === 'confirm') {
        setIsShipmentSubmit(true);
        setShowConfirmation(true);
        // Store the original shipment items for API call
        if (event.detail?.originalShipmentItems) {
          setOriginalShipmentItems(event.detail.originalShipmentItems);
        }
      }
    };

    window.addEventListener('shipment-submit-confirm', handleShipmentSubmitConfirm as EventListener);
    
    return () => {
      window.removeEventListener('shipment-submit-confirm', handleShipmentSubmitConfirm as EventListener);
    };
  }, []);

  const handleStatusSelect = (status: string) => {
    console.log('Status selected:', status);
    setSelectedStatus(status);
    
    // Reset Take-in and Ship active states when changing main status
    setIsTakeInActive(false);
    setIsShipActive(false);
    // Reset split view on status change
    setIsShipmentSubmit(false);
    setShowShipmentSubmit(false);
    setRightTableOrders([]);
    
    // Update filter store for fulfillment
    if (showFulfillmentTabs) {
      setFulfillmentStatus(status);
      setSearchParams({ tab: status });
    }

    // For report tabs, sync the main app and preserve TappyKash sub-tab when relevant
    if (showReportTabs) {
      const params: Record<string, string> = { reportTab: status };
      if (status === 'tappykash') {
        params.tappySub = selectedSubStatus || 'capture';
      } else if (status === 'voucher') {
        params.voucherSub = 'unassign';
        setSelectedSubStatus('unassign');
      }
      setSearchParams(params);
    }
    
    // For operation tabs, set default sub-status based on main status
    if (showOperationTabs) {
      let defaultSubStatus = 'on_its_way';
      if (status === 'in_facility') {
        defaultSubStatus = 'in_bound';
      } else if (status === 'awaiting') {
        defaultSubStatus = 'on_its_way';
      } else if (status === 'destination_warehouse') {
        defaultSubStatus = 'inbound';
      } else if (status === 'distribution_center') {
        defaultSubStatus = 'inbound';
      } else if (status === 'delivered') {
        defaultSubStatus = 'delivered';
      }
      console.log('LayoutWrapper setting operation status:', { status, defaultSubStatus });
      setSelectedSubStatus(defaultSubStatus);
      setSearchParams({ tab: status, subStatus: defaultSubStatus });
    }
  };

  const handleSubStatusSelect = (subStatus: string) => {
    console.log('Sub-status selected:', subStatus);
    setSelectedSubStatus(subStatus);
    
    // Reset Take-in and Ship active states when changing sub-status
    setIsTakeInActive(false);
    setIsShipActive(false);
    // Reset split context on sub-status change
    setIsShipmentSubmit(false);
    setShowShipmentSubmit(false);
    setRightTableOrders([]);
    
    // Dispatch custom event for operation page to handle sub-status clicks
    if (showOperationTabs) {
      window.dispatchEvent(new CustomEvent('sub-status-clicked', { 
        detail: { subStatus, selectedStatus } 
      }));
    }
    
    if (showFulfillmentTabs) {
      setFulfillmentSubStatus(subStatus);
    }
    // For operation tabs, update URL params to include sub-status
    if (showOperationTabs) {
      const currentTab = searchParams.get('tab') || selectedStatus || 'awaiting';
      setSearchParams({ tab: currentTab, subStatus });
    }

    // For report tabs (TappyKash only), store Capture/Report as separate query param
    if (showReportTabs && selectedStatus === 'tappykash') {
      const currentTab = searchParams.get('reportTab') || selectedStatus || 'tappykash';
      setSearchParams({ reportTab: currentTab, tappySub: subStatus });
    }

    // For report tabs (Voucher), store subtab as separate query param
    if (showReportTabs && selectedStatus === 'voucher') {
      const currentTab = searchParams.get('reportTab') || selectedStatus || 'voucher';
      setSearchParams({ reportTab: currentTab, voucherSub: subStatus });
    }
  };

  const handleTakeInClick = () => {
    setIsTakeInActive(!isTakeInActive);
    // Reset Ship active state when Take-in is clicked
    setIsShipActive(false);
    // Dispatch custom event for operation page to handle
    window.dispatchEvent(new CustomEvent('take-in-clicked', { 
      detail: { 
        isActive: !isTakeInActive,
        selectedStatus,
        selectedSubStatus
      } 
    }));
  };

  const handleShipClick = () => {
    setIsShipActive(!isShipActive);
    // Reset Take-in active state when Ship is clicked
    setIsTakeInActive(false);
    // Dispatch custom event for operation page to handle
    const newIsActive = !isShipActive;
    window.dispatchEvent(new CustomEvent('ship-clicked', { 
      detail: { 
        isActive: newIsActive,
        selectedStatus,
        selectedSubStatus
      } 
    }));
  };

  const handleSubmitNowClick = () => {
    if (rightTableOrders.length === 0) {
      addToast({
        type: 'warning',
        title: 'No Items Selected',
        message: 'No items selected to submit',
        duration: 3000
      });
      return;
    }
    setShowConfirmation(true);
  };

  const handleShipNowClick = () => {
    if (rightTableOrders.length === 0) {
      addToast({
        type: 'warning',
        title: 'No Items Selected',
        message: 'No items selected to ship',
        duration: 3000
      });
      return;
    }
    setShowConfirmation(true);
  };

  const handleOutboundSubmitClick = () => {
    // For Outbound subtab, we can handle any specific logic here
    // For now, just show a message
    addToast({
      type: 'info',
      title: 'Coming Soon',
      message: 'Outbound submit functionality will be implemented here',
      duration: 4000
    });
  };

  const handleShipmentSubmitClick = () => {
    // Check if we're in a split table context (SplitShipmentItemsTable)
    // If so, dispatch the event that the split table expects
    const splitTableElement = document.querySelector('[data-component="SplitShipmentItemsTable"]');
    if (splitTableElement) {
      const event = new CustomEvent('shipment-submit-clicked', {
        detail: { action: 'submit' }
      });
      window.dispatchEvent(event);
      return;
    }
    
    // Otherwise, use the normal shipment submit flow
    setIsShipmentSubmit(true);
    setShowConfirmation(true);
  };

    const handleConfirmSubmit = async () => {
    // setIsSubmitting(true);
    
    try {
      let response;
      
      if (isToCenterActive) {
        // Handle To Center submit - dispatch event for SplitOrdersTable to handle
        window.dispatchEvent(new CustomEvent('to-center-submit-clicked'));
        setShowConfirmation(false);
        return;
      }
      
      if (isShipmentSubmit) {
        // Handle shipment submit (arrived order)
        const orderProductIds = Array.from(new Set(
          shipmentItems.flatMap((row: any) => {
            if (row && Array.isArray(row.order_products) && row.order_products.length > 0) {
              return row.order_products
                .map((p: any) => parseInt(p.id))
                .filter((n: number) => Number.isFinite(n));
            }
            const n = parseInt(row?.id);
            return Number.isFinite(n) ? [n] : [];
          })
        ));
         
        // Get master_way_bill from the shipmentItems directly
        // Since shipmentItems contains the transformed items, we need to get the master_way_bill from the original data
        // The master_way_bill should be the same for all items in a shipment
        let masterWayBill = '';
        
        // Try to get master_way_bill from originalShipmentItems first
        if (originalShipmentItems.length > 0) {
          masterWayBill = originalShipmentItems[0]?.master_way_bill || '';
        }
        
        // If we still don't have master_way_bill, try to get it from any available shipment item
        if (!masterWayBill && shipmentItems.length > 0) {
          const firstItem = shipmentItems[0];
          if (firstItem && typeof firstItem === 'object' && 'master_way_bill' in firstItem) {
            masterWayBill = (firstItem as any).master_way_bill || '';
          }
        }
        
        // If still no master_way_bill, abort — legacy would never submit without one
        if (!masterWayBill) {
          const errorEvent = new CustomEvent('shipment-submit-error', {
            detail: { action: 'error', message: 'Cannot confirm: no master way bill found for these items.' }
          });
          window.dispatchEvent(errorEvent);
          return;
        }
        
        response = await shipmentItemApi.arrivedOrder(token || '', {
          order_product_id: orderProductIds,
          master_way_bill: masterWayBill,
          order_confirmed: 'yes',
          store_id: currentStore?.id || 1
        });
        
        if (response.success) {
          // Clear the shipment items
          setShipmentItems([]);
          setShowShipmentSubmit(false);
          
          // Dispatch success event for SplitShipmentItemsTable
          const successEvent = new CustomEvent('shipment-submit-success', {
            detail: { action: 'success' }
          });
          window.dispatchEvent(successEvent);
          
          // Emit event to notify that items are removed from the right table
          const event = new CustomEvent('shipment-items-selected', {
            detail: { hasItems: false, itemCount: 0, items: [] }
          });
          window.dispatchEvent(event);
        } else {
          // Dispatch error event for SplitShipmentItemsTable
          const errorEvent = new CustomEvent('shipment-submit-error', {
            detail: { 
              action: 'error',
              message: response.message || 'Failed to confirm items'
            }
          });
          window.dispatchEvent(errorEvent);
        }
      } else {
        // Handle regular facility submit
        // Build array of order_product IDs (item IDs). Some selections may be orders (with nested order_products)
        console.log('🔍 Debug: rightTableOrders structure:', rightTableOrders);
        
        // Build list of order product IDs; expand orders into their products
        let orderItemIds = Array.from(new Set(
          rightTableOrders.flatMap((row: any) => {
            console.log('🔍 Debug: Processing row:', row);
            
            // If the row contains nested order products (GraphQL orders), take their IDs
            if (row && Array.isArray(row.order_products) && row.order_products.length > 0) {
              console.log('🔍 Debug: Found nested order_products:', row.order_products);
              const productIds = row.order_products
                .map((p: any) => parseInt(p.id))
                .filter((n: number) => Number.isFinite(n));
              console.log('🔍 Debug: Extracted product IDs:', productIds);
              return productIds;
            }
            
            // Check if this row itself is an order product (has order_product specific fields)
            if (row && row.code && (row.first_identifier || row.second_identifier)) {
              console.log('🔍 Debug: Row appears to be an order product, using ID:', row.id);
              const n = parseInt(row?.id);
              return Number.isFinite(n) ? [n] : [];
            }
            
            // Otherwise assume the row itself is an item and has a numeric id
            console.log('🔍 Debug: Using row.id as fallback:', row?.id);
            const n = parseInt(row?.id);
            return Number.isFinite(n) ? [n] : [];
          })
        ));

        // Defensive normalization: if any IDs look like order IDs (from split table rows without products),
        // expand them into order product IDs using the data in rightTableOrders. If still unresolved,
        // fall back to fetching via GraphQL.
        if (orderItemIds.length > 0) {
          const selectedIds = new Set(orderItemIds);
          const expandedIds: number[] = [];

          // First pass: expand using already loaded rightTableOrders data
          rightTableOrders.forEach((row: any) => {
            const rowIdNum = parseInt(row?.id);
            if (!Number.isFinite(rowIdNum)) return;
            if (selectedIds.has(rowIdNum)) {
              if (Array.isArray(row.order_products) && row.order_products.length > 0) {
                row.order_products.forEach((p: any) => {
                  const pid = parseInt(p?.id);
                  if (Number.isFinite(pid)) expandedIds.push(pid);
                });
              } else if (row && row.code && (row.first_identifier || row.second_identifier)) {
                // It's already an order product row
                expandedIds.push(rowIdNum);
              } else {
                // Keep for potential GraphQL expansion if needed
              }
            }
          });

          // Include any IDs that were already product IDs but not duplicated above
          orderItemIds.forEach((id) => {
            if (!expandedIds.includes(id)) expandedIds.push(id);
          });

          orderItemIds = Array.from(new Set(expandedIds));
        }
        
        console.log('🔍 Debug: Final orderItemIds:', orderItemIds);
 
        // Determine which API to call based on the active button
        if (isShipActive) {
          // Use the full shipment flow for Ship button
          if (!currentStore?.name) {
            addToast({
              type: 'error',
              title: 'Store Error',
              message: 'Store information not available',
              duration: 4000
            });
            return;
          }
          console.log('orderItemIds', orderItemIds);
          console.log('currentStore.id', currentStore.id);
          response = await trackingApi.createShipmentFlow(token || '', orderItemIds, currentStore.id);
        } else {
          response = await trackingApi.submitToFacility(token || '', orderItemIds, currentStore?.id || 1);
        }
        
        if (response.success) {
          // Clear the right table orders
          setRightTableOrders([]);
          
          // Show success toast
          const successMessage = isShipActive 
            ? 'Items successfully moved to Outbound shipment'
            : 'Items successfully submitted to facility';
          addToast({
            type: 'success',
            title: 'Success!',
            message: successMessage,
            duration: 4000
          });
          
          // Refresh the operation page data
          window.dispatchEvent(new CustomEvent('refresh-operation-data'));
        } else {
          // Show error toast
          addToast({
            type: 'error',
            title: 'Submission Failed',
            message: response.message || 'Failed to process items',
            duration: 5000
          });
        }
      }
    } catch (err) {
      console.error('Error processing items:', err);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'An error occurred while processing items',
        duration: 5000
      });
    } finally {
      // setIsSubmitting(false);
      setShowConfirmation(false);
      setIsShipmentSubmit(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <Sidebar />
      <div className="ml-24 flex flex-col min-h-screen">
        <Header 
          selectedStatus={selectedStatus}
          selectedSubStatus={selectedSubStatus}
          onStatusSelect={handleStatusSelect}
          onSubStatusSelect={handleSubStatusSelect}
          showFulfillmentTabs={showFulfillmentTabs}
          showOperationTabs={showOperationTabs}
          showReportTabs={showReportTabs}
          isDashboard={isDashboard}
          showTakeInButton={showTakeInButton}
          isTakeInActive={isTakeInActive}
          onTakeInClick={handleTakeInClick}
          onSubmitNowClick={handleSubmitNowClick}
          showShipButton={showShipButton}
          isShipActive={isShipActive}
          onShipClick={handleShipClick}
          onShipNowClick={handleShipNowClick}
          showOutboundSubmit={showOutboundSubmit}
          onOutboundSubmitClick={handleOutboundSubmitClick}
          showShipmentSubmit={showShipmentSubmit}
          onShipmentSubmitClick={handleShipmentSubmitClick}
          // To Center controls visible only for destination_warehouse → in_warehouse
          showToCenterButton={showOperationTabs && selectedStatus === 'destination_warehouse' && selectedSubStatus === 'in_warehouse' && !isShipActive && !isTakeInActive}
          isToCenterActive={showOperationTabs && selectedStatus === 'destination_warehouse' && selectedSubStatus === 'in_warehouse' && isToCenterActive}
          onToCenterClick={() => {
            // Toggle: let the operation page manage split view, we just track active state
            if (isToCenterActive) {
              window.dispatchEvent(new CustomEvent('sub-status-clicked', { detail: { subStatus: selectedSubStatus, selectedStatus } }));
              setIsToCenterActive(false);
            } else {
              window.dispatchEvent(new CustomEvent('to-center-clicked'));
              setIsToCenterActive(true);
            }
          }}
          showToCenterSubmit={showOperationTabs && selectedStatus === 'destination_warehouse' && selectedSubStatus === 'in_warehouse' && isToCenterActive}
          onToCenterSubmitClick={() => {
            // Open the same confirmation dialog used elsewhere, but wire submit to to-center flow
            setIsShipmentSubmit(false);
            setShowConfirmation(true);
            // Temporarily hook confirm handler via a one-time event listener
            const onConfirm = () => {
              window.dispatchEvent(new CustomEvent('to-center-submit-clicked'));
              window.removeEventListener('confirm-submit-once', onConfirm as EventListener);
            };
            window.addEventListener('confirm-submit-once', onConfirm as EventListener, { once: true });
          }}
        />
        <main className="flex-1">
          <Outlet />
        </main>
      </div>

      {/* Confirmation Dialog */}
              <ConfirmationDialog
          isOpen={showConfirmation}
          onClose={handleCancelSubmit}
          onConfirm={handleConfirmSubmit}
                     title="Confirmation"
                     message={isShipmentSubmit 
            ? `Are you sure you want to confirm ${shipmentItems.length} item(s) as arrived? This action cannot be undone.`
            : isShipActive 
              ? `Are you sure you want to create an Outbound shipment for the selected ${rightTableOrders.length} items? This will move them from "In Facility" to "Outbound" status.`
              : isToCenterActive
                ? `Are you sure you want to submit the selected ${rightTableOrders.length} items to selected center?`
                : `Are you sure you want to submit the selected ${rightTableOrders.length} items to facility?`
          }
          confirmText="Submit"
          cancelText="Cancel"
        />
    </div>
  );
}
